import clsx from 'clsx'
import React, { useMemo, useState } from 'react'
import {
  BasicStyledComponent,
  DSBestListComparionTable,
  DSBestListRatingsTableContent,
  DSSortValue,
  DSSubmitProductVoteResult,
  DSVote,
} from 'shared-definitions/types'
import styles from './BestListComparisonTable.module.css'
import { BestListComparisonTableContent } from './BestListComparisonTableContent'

interface BestListComparisonTableProps extends BasicStyledComponent {
  table: DSBestListComparionTable
  defaultSortValue: DSSortValue | null
  tableContent: DSBestListRatingsTableContent
  onVote: (productId: string, vote: DSVote) => Promise<DSSubmitProductVoteResult | null>
  onSeeMore: (productId: string, signal?: AbortSignal) => void
  filterBlock: React.ReactNode
  content: React.ReactNode
}

export const BestListComparisonTable: React.FC<BestListComparisonTableProps> = ({
  className,
  table,
  tableContent,
  onSeeMore,
  onVote,
  defaultSortValue,
  filterBlock,
  content,
}) => {
  const [sortValue, setSortValue] = useState(defaultSortValue)
  // eslint-disable-next-line @typescript-eslint/no-unused-vars

  const sortedContent = useMemo(() => sortRows(tableContent, sortValue), [tableContent, sortValue])

  return (
    <section className={clsx(className, styles.container)}>
      <div className={styles.desktopHeader}>
        <h2 className={styles.title}>{table.title}</h2>
        {filterBlock}
      </div>
      {content}
      <BestListComparisonTableContent
        sortValue={sortValue}
        onSortValueChange={setSortValue}
        vote={onVote}
        onSeeMore={onSeeMore}
        className={styles.content}
        content={sortedContent}
      />
    </section>
  )
}

function sortRows(
  content: DSBestListRatingsTableContent,
  sortValue: DSSortValue | null
): DSBestListRatingsTableContent {
  if (!sortValue) {
    return content
  }

  const columnIndex = content.columns.findIndex(column => column.columnId === sortValue.field)
  if (columnIndex === -1) {
    return content
  }

  const sortedRows = [...content.rows].sort((a, b) => {
    const aCell = a.cells[columnIndex],
      bCell = b.cells[columnIndex]
    if (aCell.type === 'numeric' && bCell.type === 'numeric') {
      if (sortValue.order === 'asc') {
        return aCell.value - bCell.value
      }
      return bCell.value - aCell.value
    }

    if (aCell.type === 'text' && bCell.type === 'text') {
      if (sortValue.order === 'asc') {
        return aCell.text.localeCompare(bCell.text)
      }
      return bCell.text.localeCompare(aCell.text)
    }

    return 0
  })

  return { ...content, rows: sortedRows }
}
